/**
 * Loom onboarding step manager.
 *
 * Single source of truth for "which screens does this user see, in what
 * order?". Everything variant- or user-specific about the flow lives here.
 *
 * ─── How to edit ────────────────────────────────────────────────────────
 *  - Add / reorder screens in a variant:  edit `STEPS_BY_VARIANT`.
 *  - Branch on user data (persona, cloud
 *    state, feature flags, etc.):         extend `useStepsForUser` — that's
 *                                         the only place path resolution
 *                                         happens.
 *  - Add a new step surface entirely:     add the constant and component
 *                                         map in `./types.ts`, then
 *                                         reference the constant from any
 *                                         variant list below.
 *
 * The view (`loom-onboarding-view.tsx`) knows nothing about which steps
 * exist, what order they run in, or which experiment gates them — it just
 * renders `<StepManager />`.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIsAnalyticsReady } from '@/analytics/wac-analytics/analytics-client';
import { ufoEventLoomOnboardingFlowDuration } from '@/components/signup/utils/ufo-events';

import { useCloudStatus } from '../../../hooks/use-cloud-status';
import { CornerLoomLogo } from '../components/corner-loom-logo';
import { GradientBackground } from '../components/gradient-background';

import { useLoomOnboardingExperiment, LoomOnboardingVariants } from './is-exp-loom-onboarding';
import { useLoomOnboardingAnalytics } from './loom-onboarding-analytics';
import {
	onboardingSource,
	ONBOARDING_STEP_COMPONENTS,
	type OnboardingStep,
	StepCompletionPayload,
} from './types';

/**
 * Ordered list of steps a user sees, per experiment variant. Order in each
 * array IS the flow order.
 *
 * `CONTROL` intentionally has no steps — a control user shouldn't reach the
 * onboarding view at all (see `product-view-switch.tsx`); the empty list
 * makes the fallback render nothing instead of crashing if it does.
 */

const STEPS_BY_VARIANT: Record<LoomOnboardingVariants, readonly OnboardingStep[]> = {
	[LoomOnboardingVariants.VARIANT_1]: ['persona-use-case-step', 'download-options-step'],
	[LoomOnboardingVariants.CONTROL]: [],
};

/**
 * Resolves the ordered list of steps for the current user.
 *
 * Right now this only branches on the experiment variant. To also gate on
 * user data extend this one hook.
 */
const useStepsForUser = (): readonly OnboardingStep[] => {
	const { loomOnboardingToWacVariant } = useLoomOnboardingExperiment();
	return useMemo(
		() => (loomOnboardingToWacVariant ? STEPS_BY_VARIANT[loomOnboardingToWacVariant] : []),
		[loomOnboardingToWacVariant],
	);
};

type StepManagerProps = {
	/**
	 * Called when the user continues from the last step. The step manager is
	 * agnostic to what this does — the onboardingview owns cloud-status / redirect
	 * logic.
	 */
	readonly handleContinue: () => void;
};

/**
 * Renders the component for whichever step the user is
 * currently on.
 * Owns the "where am I in the flow?" state; the view above
 * owns everything else (errors, redirects, cloud status, telemetry).
 */
export const StepManager = ({ handleContinue }: StepManagerProps) => {
	const steps = useStepsForUser();
	const [index, setIndex] = useState(0);
	const { fireOnboardingStarted, fireStepViewed, fireStepCompleted, fireUiViewed } =
		useLoomOnboardingAnalytics();

	const currentStep = steps[index] ?? steps[0];

	// Hold analytics events until they're guaranteed to be delivered (see
	// `useIsAnalyticsReady`), rather than firing on `analyticsOptOut` alone.
	// Every step visit / completion is still recorded as it happens and
	// flushed in order once ready, so a fast user who advances before that
	// gets a `viewed`/`completed` for each step they actually hit.
	const isAnalyticsReady = useIsAnalyticsReady();

	// Best-effort tenant for the loom-product `ui viewed` event. Provisioning
	// is only just starting when the user lands here, so this is frequently
	// still empty — the event must NOT wait for it. It is read at the render
	// the effect below fires on, and is intentionally not an effect dependency
	// (a late-arriving cloudId must never re-fire the event).
	const { cloudId } = useCloudStatus();

	const lastRecordedViewKeyRef = useRef<string | null>(null);
	// Queued events fired while `isAnalyticsReady` is false. Shared by
	// `viewed` and `completed` so both flush in real chronological order.
	const pendingEventsRef = useRef<(() => void)[]>([]);
	const hasStartedUfoRef = useRef(false);
	const hasFiredInitiatedRef = useRef(false);
	const hasFiredUiViewedRef = useRef(false);

	// UFO timer is not an analytics event, so it is not consent-gated.
	useEffect(() => {
		if (!currentStep || hasStartedUfoRef.current) return;
		hasStartedUfoRef.current = true;
		void ufoEventLoomOnboardingFlowDuration.start();
	}, [currentStep]);

	useEffect(() => {
		if (!currentStep) return;

		// Record this step visit. Keyed on `(currentStep, index)` so
		// back-and-forward navigation each produces a fresh visit, but any
		// re-run of this effect for the same visit is a no-op.
		const viewKey = `${currentStep}#${index}`;
		if (lastRecordedViewKeyRef.current !== viewKey) {
			lastRecordedViewKeyRef.current = viewKey;
			const step = currentStep;
			const stepIndex = index;
			pendingEventsRef.current.push(() =>
				fireStepViewed(
					{
						flowId: 'default',
						source: onboardingSource,
						step,
						stepIndex,
					},
					stepIndex === 0,
				),
			);
		}

		if (!isAnalyticsReady) return;

		// Once-per-flow fire. Fired before any `viewed` so it always leads the
		// funnel, and as soon as consent allows even if the user has already
		// moved past the first step.
		if (!hasFiredInitiatedRef.current) {
			hasFiredInitiatedRef.current = true;
			fireOnboardingStarted({
				flowId: 'default',
				source: onboardingSource,
			});
		}

		// Once-per-flow fire of the loom-product `ui viewed` event. This is the MAU
		// event.
		if (!hasFiredUiViewedRef.current) {
			hasFiredUiViewedRef.current = true;
			fireUiViewed(cloudId || undefined);
		}

		// Flush queued events oldest first.
		const pendingEvents = pendingEventsRef.current;
		pendingEventsRef.current = [];
		pendingEvents.forEach((fireEvent) => fireEvent());
		// `fireStepViewed` / `fireOnboardingStarted` / `fireUiViewed` are stable via
		// `useCallback([createAnalyticsEvent])`; intentionally omitted from
		// deps so this effect is driven only by the primitive
		// `(currentStep, index)` tuple the user actually sees, plus analytics
		// readiness.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, index, isAnalyticsReady]);

	const onContinue = useCallback(
		(payload?: StepCompletionPayload) => {
			const completedStep = steps[index];
			if (completedStep) {
				const stepIndex = index;
				const fireCompleted = () =>
					fireStepCompleted(
						{
							flowId: 'default',
							source: onboardingSource,
							step: completedStep,
							stepIndex,
						},
						payload?.action,
						payload?.persona,
					);

				// Same as `viewed` above: hold `completed` until delivery is
				// guaranteed instead of risking a silent drop.
				if (isAnalyticsReady) {
					fireCompleted();
				} else {
					pendingEventsRef.current.push(fireCompleted);
				}
			}

			if (index >= steps.length - 1) {
				// Last step — the flow is done. `onboardingFlow completed` is
				// fired from `AfterOnboardingStep`, where the provisioning state
				// is available to derive `hasProvisioningTimeLeft`.
				handleContinue();
				return;
			}
			setIndex((current) => current + 1);
		},
		[fireStepCompleted, handleContinue, index, isAnalyticsReady, steps],
	);

	const canGoBack = index > 0;
	const onBack = useCallback(() => {
		setIndex((current) => (current > 0 ? current - 1 : current));
	}, []);

	if (steps.length === 0) {
		// Defensive: `product-view-switch` should never route a stepless
		// variant here, but if it does we'd rather render nothing than an
		// empty shell with a broken progress bar.
		return null;
	}

	const StepComponent = ONBOARDING_STEP_COMPONENTS[currentStep as OnboardingStep];

	return (
		<Fragment>
			<CornerLoomLogo includeBackButton={canGoBack} onBack={onBack} />
			<GradientBackground>
				<StepComponent onContinue={onContinue} />
			</GradientBackground>
		</Fragment>
	);
};
